import {
  FieldArray,
  Formik,
  FormikErrors,
  FormikHandlers,
  FormikProps,
  FormikTouched,
  FormikValues,
  getIn,
} from 'formik';
import get from 'lodash.get';
import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { BsChevronLeft } from 'react-icons/bs';
import { IoIosCamera } from 'react-icons/io';
import { RiStore2Line } from 'react-icons/ri';
import { toast } from 'react-toastify';

import apiUtil from 'src/api/businessOnboarding';
import CustomToast from 'src/components/CustomToast';
import Icon from 'src/components/Icon';
import ImgUpload, { ImageCallback } from 'src/components/ImgUpload';
import { BUSINESSE_CREATE_ERROR } from 'src/containers/Core/Messages';
import { requiredSchema } from 'src/validationSchemas';
import { array, object, string } from 'yup';
import { BUSINESS_ONBOARDING_STEP } from '.';
import { days } from './Components/BusinessProfile';

import './style.scss';

const isEmpty = require('lodash.isempty');

export const defaultOpenBusinessHours = days.map((day: string) => ({
  day,
  isOpen: false,
  startTime: null,
  endTime: null,
}));

const compareTimeSchema = () =>
  string()
    .nullable(true)
    // tslint:disable-next-line:no-any
    .when(['isOpen'], (isOpen: boolean, schema1: any) => {
      if (isOpen) {
        return schema1.test({
          test: (val: Date | string) => val,
          message: 'Required',
        });
      } else {
        return schema1.nullable().notRequired();
      }
    });

const getAddMoreProps = (valuesArr: string[], fn: () => void): { className: string; onClick?: () => void } => {
  if (!!valuesArr[valuesArr.length - 1]) {
    return {
      className: 'c-pointer text-primary',
      onClick: fn,
    };
  } else {
    return {
      className: 'text-muted',
    };
  }
};

const getDefaultInitialValues: () => EntityFields = () => ({
  name: '',
  displayName: '',
  emailAddress: '',
  website: '',
  address: {
    address1: '',
    address2: '',
    state: '',
    city: '',
    country: 'United States',
    zipCode: '',
  },
  phoneNumber: [],
  uuid: '',
  community_business: '',
  logo: null,
  businessOpeningHours: defaultOpenBusinessHours,
  notes: '',
  openExternalLinks: [],
});

const schema = object({
  name: requiredSchema.max(255).nullable(),
  address: object({
    address1: requiredSchema.max(1024).nullable(),
    zipCode: requiredSchema
      .matches(/^[0-9]{5}(-[0-9]{1,5})*$/, 'Invalid ZipCode')
      .min(5, 'ZipCode should have minimum 5 digits long')
      .max(50),
    city: requiredSchema.max(255),
    state: requiredSchema.max(255),
  }),
  phoneNumber: array()
    .of(string().max(50))
    .nullable(),
  businessOpeningHours: array().of(
    object().shape(
      {
        day: string().nullable(),
        startTime: compareTimeSchema(),
        endTime: compareTimeSchema(),
      },
      [['isOpen', 'day']]
    )
  ),
  businessHourNotes: string()
    .max(1024)
    .nullable(),
  website: string()
    .max(1024)
    .nullable()
    .matches(
      // tslint:disable-next-line:max-line-length
      /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-zA-Z0-9]+([\-\.]{1}[a-zA-Z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/[^,;\s]+)?\/?$/,
      'Website should be a valid URL'
    ),
});

const getFirstChildObject = (arr: object[]) => {
  // tslint:disable-next-line:prefer-for-of
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] && !isEmpty(arr[i])) {
      return {
        idx: i,
        val: arr[i],
      };
    }
  }

  return { idx: 0, val: {} };
};

// tslint:disable-next-line:no-any
const getFirstErrorKeyPath: any = (errors: any) => {
  const keys = Object.keys(errors);
  if (!keys.length) {
    return '';
  }

  if (typeof errors[keys[0]] === 'string') {
    return keys[0];
  } else if (Array.isArray(errors[keys[0]])) {
    if (errors[keys[0]].length === 1 && typeof errors[keys[0]][0] === 'string') {
      return keys[0];
    }

    const obj = getFirstChildObject(errors[keys[0]]);

    return `${keys[0]}.${obj.idx}.${getFirstErrorKeyPath(obj.val)}`;
  } else {
    return `${keys[0]}.${getFirstErrorKeyPath(errors[keys[0]])}`;
  }
};

type CreateBusinessProfileProps = {
  setOnboardingStep: (step: BUSINESS_ONBOARDING_STEP, businessId?: string) => void;
};

const CreateBusinessProfile: React.FunctionComponent<CreateBusinessProfileProps> = ({ setOnboardingStep }) => {
  const [imgCallback, setImgCallback] = useState<FormData>(null);
  const [existingBusinessCount, setExistingBusinessCount] = useState(0);

  useEffect(() => {
    const fetchExistingBusinesses = async () => {
      const payload = await apiUtil.getExistingBusinesses();
      setExistingBusinessCount(payload.count);
    };

    fetchExistingBusinesses();
  }, []);

  // tslint:disable-next-line:no-any
  const onSubmit = async (fields: EntityFields, actions: any) => {
    try {
      const data = { ...fields };
      if (get(data, 'businessOpeningHours', []).length > 0) {
        const openBusinessHours: BusinessOpeningHours[] = [];
        data.businessOpeningHours.forEach((openBusinessHour: BusinessOpeningHours, index) => {
          const newBusinessHour: BusinessOpeningHours = {
            day: index + 1,
            startTime: '',
            endTime: '',
          };

          ['startTime', 'endTime'].forEach(key => {
            // @ts-ignore
            newBusinessHour[key] = get(openBusinessHour, key)
              ? `${get(openBusinessHour, key).getHours()}:${get(openBusinessHour, key).getMinutes()}`
              : null;
          });
          if (openBusinessHour.isOpen && (openBusinessHour.startTime || openBusinessHour.endTime)) {
            openBusinessHours.push(newBusinessHour);
          }
        });

        data.businessOpeningHours = openBusinessHours;
      }

      const payload = await apiUtil.createBusiness(data);

      if (imgCallback) {
        await apiUtil.addBusinessLogo(imgCallback, payload.uuid);
      }
      setOnboardingStep(BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_MATCHING, payload.uuid);
    } catch (errors) {
      toast(<CustomToast title="New Business Creation" description={BUSINESSE_CREATE_ERROR} />);
      actions.setErrors(errors);
    }

    actions.setSubmitting(false);
  };

  return (
    <div className="overflow-auto p-3 business-profile-wrapper">
      {existingBusinessCount > 0 ? (
        <span
          className="c-pointer royal-blue-color"
          onClick={() => setOnboardingStep(BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_OWNED)}
        >
          <span className="mr-1">
            <BsChevronLeft size={11} />
          </span>
          <small>Back to business list</small>
        </span>
      ) : null}
      <div className="mt-1">
        <strong>Create Business Profile</strong>
        <div className="mt-3">
          <Row>
            <Col md={6}>
              <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={getDefaultInitialValues()}>
                {props => (
                  <BusinessForm {...props} setImgCallback={setImgCallback} initialValues={getDefaultInitialValues()} />
                )}
              </Formik>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default CreateBusinessProfile;

const BusinessForm: React.FunctionComponent<
  FormikProps<EntityFields> &
    FormikHandlers & {
      setImgCallback: ImageCallback;
      initialValues: EntityFields;
    }
> = props => {
  const {
    errors,
    handleChange,
    values,
    handleSubmit,
    touched,
    isSubmitting,
    setFieldValue,
    dirty,
    setImgCallback,
  } = props;

  const [cities, setCities] = useState([]);
  const [provincesData, setProvincesData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProvinces = async () => {
      setLoading(true);
      // tslint:disable-next-line:no-any
      const response: any = await apiUtil.getStatesGeoData();
      setLoading(false);
      setProvincesData(response);
      setFieldValue('address.state', response[0][1]);
    };
    fetchProvinces();
  }, []);

  useEffect(() => {
    try {
      const state = provincesData.find(p => p[1] === get(values, 'address.state'));
      const fetchCities = async () => {
        setLoading(true);
        if (state) {
          setFieldValue('address.stateShortCode', state[0]);
        }
        const response: string[] = await apiUtil.getCitiesGeoData(state);
        setLoading(false);
        setCities(response);
        setFieldValue('address.city', response[0][0]);
      };

      if (state) {
        fetchCities();
      }
    } catch (e) {
      // do something
    }
  }, [get(values, 'address.state')]);

  const PlaceHolderImage = (
    <div
      className={`profile-placeholder d-flex align-items-center justify-content-center rounded-circle
      position-relative p-3 bg-light c-pointer`}
    >
      <span>
        <RiStore2Line size={24} className="royal-blue-color" />
      </span>
      <span
        className="d-inline-block position-absolute p-1 bg-white rounded-circle text-muted"
        style={{
          bottom: 0,
          right: 0,
          lineHeight: 0.5,
        }}
      >
        <IoIosCamera size={18} />
      </span>
    </div>
  );

  return (
    <Form noValidate onSubmit={handleSubmit}>
      <Form.Group controlId="BusinessLogo">
        <div className="d-flex flex-column align-items-center">
          <ImgUpload
            className="businesslogo-placeholder"
            onSubmitCallback={(data: FormData) => {
              setFieldValue('logo', data.get('logo'));
              setImgCallback(data);
            }}
            customPlaceholder={PlaceHolderImage}
            img={values.logo}
            objkey="logo"
          />
          <small className="text-muted mt-2">Business Logo</small>
        </div>
      </Form.Group>
      <Form.Group controlId="Name">
        <Form.Label id="name" className="required">
          Business Name
        </Form.Label>
        <Form.Control
          value={values.name}
          type="text"
          placeholder="Enter name"
          onChange={handleChange}
          isInvalid={touched.name && !!errors.name}
          autoComplete="off"
          name="name"
          disabled={isSubmitting}
          maxLength={255}
        />
        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group controlId="Address1">
        <Form.Label id="address.address1" className="required">
          Street Address
        </Form.Label>
        <Form.Control
          value={values.address.address1}
          type="text"
          placeholder="Enter your address"
          onChange={handleChange}
          isInvalid={getIn(touched, 'address.address1') && getIn(errors, 'address.address1')}
          autoComplete="off"
          name="address.address1"
          maxLength={255}
          disabled={isSubmitting}
        />
        <Form.Control.Feedback type="invalid">{getIn(errors, 'address.address1')}</Form.Control.Feedback>
      </Form.Group>

      <Row>
        <Col xs={12} sm={12} lg={6}>
          <Form.Group controlId="State">
            <Form.Label id="address.state" className="required">
              State
            </Form.Label>
            <Form.Control
              value={values.address.state}
              as="select"
              onChange={handleChange}
              autoComplete="off"
              name="address.state"
              isInvalid={getIn(touched, 'address.state') && getIn(errors, 'address.state')}
              disabled={loading || isSubmitting || !provincesData.length}
            >
              {provincesData.map((state, index) => (
                <option key={`${state[0]}-${index}`}>{state[1]}</option>
              ))}
            </Form.Control>
            <Form.Control.Feedback>{getIn(errors, 'address.state')}</Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <Form.Group controlId="City">
            <Form.Label id="address.city" className="required">
              City
            </Form.Label>
            <Form.Control
              value={values.address.city}
              as="select"
              onChange={handleChange}
              autoComplete="off"
              name="address.city"
              isInvalid={getIn(touched, 'address.city') && getIn(errors, 'address.city')}
              disabled={loading || isSubmitting || !cities.length}
              maxLength={255}
            >
              {cities.map(city => (
                <option key={city[0]} value={city[0]}>
                  {city[0]}
                </option>
              ))}
              <Form.Control.Feedback>{getIn(errors, 'address.city')}</Form.Control.Feedback>
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col xs={12} sm={12} lg={6}>
          <Form.Group controlId="Zipcode">
            <Form.Label id="address.zipCode" className="required">
              Zipcode
            </Form.Label>
            <Form.Control
              value={values.address.zipCode}
              type="text"
              placeholder="Enter Zip code"
              onChange={handleChange}
              isInvalid={getIn(touched, 'address.zipCode') && getIn(errors, 'address.zipCode')}
              autoComplete="off"
              name="address.zipCode"
              disabled={isSubmitting}
              maxLength={50}
            />
            <Form.Control.Feedback type="invalid">{getIn(errors, 'address.zipCode')}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group controlId="Website">
        <Form.Label id="website">Website</Form.Label>
        <Form.Control
          value={values.website}
          type="text"
          placeholder="Enter website URL"
          onChange={handleChange}
          isInvalid={touched.website && !!errors.website}
          autoComplete="off"
          name="website"
          disabled={isSubmitting}
          maxLength={1024}
        />
        <Form.Control.Feedback type="invalid">{errors.website}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group controlId="PhoneNumber">
        <Form.Label id="phoneNumber">Business Phone</Form.Label>
        <FieldArray name="phoneNumber">
          {helper => (
            <>
              {(values.phoneNumber.length ? values.phoneNumber : ['']).map((phoneNumber, index) => (
                <React.Fragment key={`phoneNumber-fragment-${index}`}>
                  <Form.Label key={`phoneNumber-label-${index}`}>{''}</Form.Label>
                  <Form.Control
                    key={`phoneNumber-${index}`}
                    value={phoneNumber}
                    type="text"
                    placeholder="9999999999"
                    onChange={(e: React.ChangeEvent<{ value: string }>) => {
                      handleChange(e);
                      if (values.phoneNumber.length === 1 && !e.target.value) {
                        setFieldValue('phoneNumber', []);
                      }
                    }}
                    isInvalid={getIn(touched, `phoneNumber[${index}]`) && getIn(errors, `phoneNumber[${index}]`)}
                    autoComplete="off"
                    name={`phoneNumber[${index}]`}
                    disabled={isSubmitting}
                    maxLength={50}
                  />
                  <Form.Control.Feedback type="invalid">{getIn(errors, `phoneNumber[${index}]`)}</Form.Control.Feedback>
                  {values.phoneNumber.length > 1 ? (
                    <Icon
                      icon="cancel"
                      className="hover-color-danger float-right c-pointer bg-white rounded"
                      style={{ marginTop: '-45px', marginRight: '-5px' }}
                      onClick={() => helper.remove(index)}
                    />
                  ) : null}
                </React.Fragment>
              ))}
              <small {...getAddMoreProps(values.phoneNumber, () => helper.push(''))}>+ Add Another</small>
            </>
          )}
        </FieldArray>
      </Form.Group>

      <Form.Group>
        <Form.Label>Business Hours</Form.Label>
        <BusinessHoursComponent
          fieldName="businessOpeningHours"
          values={values}
          setFieldValue={setFieldValue}
          handleChange={handleChange}
          touched={touched}
          errors={errors}
          isSubmitting={isSubmitting}
        />
      </Form.Group>

      <Form.Group>
        <Button
          variant="primary"
          type="submit"
          // scrollIntoView not working on disabled elements. So removed disabled prop
          className={`mt-2 ${!dirty || isSubmitting ? 'disabled-button' : ''}`}
          style={{ minWidth: '100px' }}
          onClick={async (e: React.SyntheticEvent) => {
            if (!(!dirty || isSubmitting)) {
              const curErrors = await props.validateForm();
              const errorKeyPath = getFirstErrorKeyPath(curErrors);

              if (errorKeyPath) {
                const el = document.getElementById(errorKeyPath);

                if (el) {
                  el.scrollIntoView({ behavior: 'smooth' });
                }
              }
            } else {
              e.preventDefault();
            }
          }}
        >
          {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Create Business Profile'}
        </Button>
      </Form.Group>
    </Form>
  );
};

type BusinessHoursComponentProps = {
  fieldName: string;
  values: FormikValues;
  setFieldValue: (field: string, value: string) => void;
  handleChange: (e: React.ChangeEvent) => void;
  touched: FormikTouched<FormikValues>;
  isSubmitting: boolean;
  errors: FormikErrors<FormikValues>;
  hideNotes?: boolean;
};

export const BusinessHoursComponent: React.FunctionComponent<BusinessHoursComponentProps> = ({
  fieldName,
  values,
  setFieldValue,
  handleChange,
  touched,
  errors,
  isSubmitting,
  hideNotes,
}) => {
  return (
    <div className="light-background light-border p-3">
      <FieldArray name={fieldName}>
        {_helper => (
          <>
            {/* tslint:disable-next-line:no-any */}
            {get(values, `${fieldName}`, []).map((day: any, index: number) => (
              <React.Fragment key={`hours-fragment-${day.day}`}>
                <BusinessHoursComponentRow
                  index={index}
                  day={day.day}
                  fieldName={fieldName}
                  values={values}
                  setFieldValue={setFieldValue}
                  touched={touched}
                  errors={errors}
                  isSubmitting={isSubmitting}
                />
              </React.Fragment>
            ))}
          </>
        )}
      </FieldArray>
      {hideNotes ? null : (
        <>
          <Form.Control
            id="businessHourNotes"
            as="textarea"
            value={get(values, 'businessHourNotes')}
            type="text"
            placeholder="Enter additional business hours notes"
            onChange={handleChange}
            isInvalid={getIn(touched, 'businessHourNotes') && !!getIn(errors, 'businessHourNotes')}
            autoComplete="off"
            name="businessHourNotes"
            disabled={isSubmitting}
            maxLength={1024}
          />
          <Form.Control.Feedback type="invalid">{getIn(errors, 'businessHourNotes')}</Form.Control.Feedback>
        </>
      )}
    </div>
  );
};

type BusinessHoursComponentRowProps = {
  index: number;
  day: string;
  fieldName: string;
  values: FormikValues;
  setFieldValue: (field: string, value: string) => void;
  touched: FormikTouched<FormikValues>;
  isSubmitting: boolean;
  errors: FormikErrors<FormikValues>;
};

const BusinessHoursComponentRow: React.FunctionComponent<BusinessHoursComponentRowProps> = ({
  index,
  day,
  fieldName,
  values,
  setFieldValue,
  touched,
  errors,
  isSubmitting,
}) => {
  const isOpen = get(values, `${fieldName}.${index}.isOpen`, false);

  return (
    <>
      <Row>
        <Col xs={6}>
          <Row className="pt-2 pb-3">
            <Col md={6}>
              <strong>{day}</strong>
            </Col>
            <Col md={6}>
              <div className="custom-control custom-switch">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id={`customSwitches-${index}`}
                  checked={isOpen}
                  onChange={(e: React.ChangeEvent) => {
                    // @ts-ignore
                    const newValue = e.currentTarget.checked;
                    setFieldValue(`${fieldName}[${index}].isOpen`, newValue);
                    if (!newValue) {
                      // @ts-ignore
                      setFieldValue(`${fieldName}[${index}].startTime`, '');
                      setFieldValue(`${fieldName}[${index}].endTime`, '');
                    }
                  }}
                />
                <label className="custom-control-label" htmlFor={`customSwitches-${index}`}>
                  {isOpen ? 'Open' : 'Closed'}
                </label>
              </div>
            </Col>
          </Row>
        </Col>
        <Col xs={6} className="d-flex">
          {isOpen ? (
            <div className="d-flex">
              <div className="mr-3">
                <DatePicker
                  id={`${fieldName}.${index}.startTime`}
                  selected={get(values, `${fieldName}.${index}.startTime`)}
                  // @ts-ignore
                  onChange={(value: Date) => setFieldValue(`${fieldName}.${index}.startTime`, value)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  placeholderText="hh:mm"
                  className={`form-control ${
                    getIn(errors, `${fieldName}.${index}.startTime`) &&
                    getIn(touched, `${fieldName}.${index}.startTime`)
                      ? 'is-invalid'
                      : ''
                  }`}
                  disabled={isSubmitting}
                  autoComplete="autoComplete"
                />
                <div className="invalid-feedback-error">
                  {getIn(touched, `${fieldName}.${index}.startTime`)
                    ? getIn(errors, `${fieldName}.${index}.startTime`)
                    : ''}
                </div>
              </div>
              <div>
                <DatePicker
                  id={`${fieldName}.${index}.endTime`}
                  selected={get(values, `${fieldName}.${index}.endTime`)}
                  // @ts-ignore
                  onChange={(value: Date) => setFieldValue(`${fieldName}.${index}.endTime`, value)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  placeholderText="hh:mm"
                  className={`form-control ${
                    getIn(errors, `${fieldName}.${index}.endTime`) && getIn(touched, `${fieldName}.${index}.endTime`)
                      ? 'is-invalid'
                      : ''
                  }`}
                  disabled={isSubmitting}
                  autoComplete="autoComplete"
                />
                <div className="invalid-feedback-error">
                  {getIn(touched, `${fieldName}.${index}.endTime`)
                    ? getIn(errors, `${fieldName}.${index}.endTime`)
                    : ''}
                </div>
              </div>
            </div>
          ) : null}
        </Col>
      </Row>
    </>
  );
};
